import { planCdtLevels, type CdtLevelPlan, type CdtLevelPlanOptions } from "./cdtLevelPlan";
import { rowLevelsOf, wallReadingOf } from "./horizontalBlocking";
import {
  branchStacksRows,
  effectiveAttrs,
  isHorizontalWall,
  isPlaceholderValue,
  levelsForScope,
  type SpineLevel,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
  type WallBlocking,
} from "./studioTypes";

export type CompressionStrategy =
  | { mode?: "tree" }
  | { mode: "measured"; options?: CdtLevelPlanOptions };

interface HorizontalTree {
  /** The section above the brand; null when the brand is the root. */
  sectionAttr: string | null;
  branchAttr: string;
  wall: WallBlocking;
}

/** Where the wall inference read as horizontal (or the merchant set so) sits in this tree.
 *  The reading is the draft's, never re-measured here: the ship draft renames the section by the rows it
 *  carves, and read again on those names the wall would no longer look horizontal. */
const horizontalTreeOf = (
  draft: StudioDraft,
  declared: readonly SpineLevel[],
): HorizontalTree | null => {
  const wall = draft.wallBlocking;
  if (!wall || !isHorizontalWall(draft)) return null;
  const depth = declared.findIndex((level) => level.attr === wall.branchAttr);
  return {
    // The brand under a section; anywhere else the tree's own tiers stand.
    sectionAttr: depth === 1 ? declared[0].attr : null,
    branchAttr: wall.branchAttr,
    wall,
  };
};

/** Developer trace, like every plan warning. The merchant reads the wall off `wallReading`,
 *  translated where the studio renders it; a merchant-set wall has no measurement to cite. */
const horizontalWarnings = (tree: HorizontalTree): string[] => {
  if (tree.wall.read !== "horizontal") return [];
  const groups = Object.values(tree.wall.branches);
  const { branchScore, branchBand } = tree.wall;
  const band = branchBand === undefined ? "" : `, row score ${branchBand.toFixed(2)}`;
  return [
    `The base pog blocks horizontally: "${tree.branchAttr}" column score ${branchScore.toFixed(2)}${band}; ${groups.filter(Boolean).length} of ${groups.length} ${tree.branchAttr} groups (${(100 * tree.wall.share).toFixed(0)}% of SKUs) stack their levels in rows.`,
  ];
};

/** cdt0 section, cdt1 brand, cdt2 the brand's leading row levels compounded ("Stick : Clean
 *  Comfort"), cdt3 the rest in tree order. Branches are brands, not the constant section. */
const horizontalBranches = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  tree: HorizontalTree & { sectionAttr: string },
): CdtLevelPlan["branches"] => {
  const rawOf = new Map(items.map((item) => [item.skuId, item]));
  const resolved = items.map((item) => ({ ...item, attrs: effectiveAttrs(item, draft) }));
  const groups = new Map<string, StudioItem[]>();
  for (const item of resolved) {
    const value = String(item.attrs[tree.branchAttr] ?? "");
    const group = groups.get(value);
    if (group) group.push(item);
    else groups.set(value, [item]);
  }
  return [...groups].map(([value, branchItems]) => {
    const section = String(branchItems[0]?.attrs[tree.sectionAttr] ?? "");
    // By attribute, not by position: a carved section ("Deodorant A") carries the brand's
    // order on its own one-segment key, where the brand is not a rung.
    const carried = levelsForScope(draft.spine, draft.scopedLevelOrder, [section, value]).filter(
      (level) =>
        level.attr !== tree.sectionAttr &&
        level.attr !== tree.branchAttr &&
        branchItems.some((item) => !isPlaceholderValue(String(item.attrs[level.attr] ?? ""))),
    );
    // A level constant inside this brand splits nothing here: it follows the ones that do.
    const splits = (level: SpineLevel) =>
      new Set(branchItems.map((item) => String(item.attrs[level.attr] ?? ""))).size > 1;
    const below = [...carried.filter(splits), ...carried.filter((level) => !splits(level))];
    // The reading names brands as the base pog does, before any retag.
    const baseName = String(rawOf.get(branchItems[0]?.skuId ?? "")?.attrs[tree.branchAttr] ?? "");
    const rowsInside = branchStacksRows(tree.wall, value, baseName);
    const rows = rowsInside
      ? rowLevelsOf(
          branchItems,
          below.map((level) => level.attr),
          runs,
          (attr) => below.findIndex((level) => level.attr === attr),
        )
      : [];
    // The tree's own leading levels, while they are the rows the wall measures here.
    const leading: SpineLevel[] = [];
    for (const level of below) {
      if (!rows.includes(level.attr)) break;
      leading.push(level);
    }
    const band = leading.length > 0 ? leading : below.slice(0, 1);
    return {
      value,
      skus: branchItems.length,
      tiers: [
        [tree.sectionAttr],
        [tree.branchAttr],
        band.map((level) => level.attr),
        below.filter((level) => !band.includes(level)).map((level) => level.attr),
      ],
      because: [
        "Preserve tree CDT0",
        "Brand: its rows stack inside its bays",
        leading.length > 0
          ? `Rows the base pog blocks horizontally: ${leading.map((level) => level.attr).join(" + ")}`
          : "Preserve next tree level",
        "Retain remaining levels in tree order",
      ],
    };
  });
};

export const planCompression = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[] = [],
  strategy: CompressionStrategy = {},
): CdtLevelPlan => {
  if (strategy.mode === "measured") return planCdtLevels(items, draft, runs, strategy.options);
  const declared = levelsForScope(draft.spine, draft.scopedLevelOrder, []);
  const horizontal = horizontalTreeOf(draft, declared);
  const sectionAttr = horizontal?.sectionAttr;
  if (horizontal && sectionAttr) {
    const branches = horizontalBranches(items, draft, runs, { ...horizontal, sectionAttr });
    const levels: CdtLevelPlan["levels"] = [0, 1, 2, 3].map((tier) => {
      const attrs = new Set(branches.flatMap((branch) => branch.tiers[tier]));
      return {
        levels: draft.spine.filter((level) => attrs.has(level.attr)),
        // Every tier below the section stacks: rows, not columns.
        orient: tier === 0 ? "horizontal" : "vertical",
        role: tier === 0 ? "vertical" : "horizontal",
        because:
          tier === 0
            ? "Preserve tree grouping"
            : tier === 3
              ? "Compress remaining tree levels without dropping attributes"
              : "Rows the base pog blocks horizontally",
      };
    });
    return {
      levels,
      branches,
      branchAttr: horizontal.branchAttr,
      blocking: "horizontal",
      wallReading: wallReadingOf(horizontal.wall),
      verticality: [],
      bandedness: [],
      needsRuling: [],
      demoted: [],
      inert: [],
      warnings: horizontalWarnings(horizontal).map(
        (warning) => `${warning} CDT2 carries those rows.`,
      ),
    };
  }
  const root = declared[0];
  const branches = root
    ? [...new Set(items.map((item) => String(effectiveAttrs(item, draft)[root.attr] ?? "")))].map(
        (value) => {
          const branchItems = items.filter(
            (item) => String(effectiveAttrs(item, draft)[root.attr] ?? "") === value,
          );
          // A band already spent on cdt0 must not take cdt1 as well: one "Sheets" column
          // for the whole zone would push the brand down a tier. A section's residual zone
          // carries no band of its own, so a sibling's band counts for it.
          const strata = draft.measuredBands?.strata ?? [];
          const own = strata.find((stratum) => stratum.cdt0 === value);
          // A stratum names the band by key or by the master's column label; fold both.
          const fold = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");
          const absorbed = new Set(
            strata
              .filter((stratum) => stratum.bandAttr && (!own || stratum.section === own.section))
              .map((stratum) => fold(stratum.bandAttr as string)),
          );
          const valuesOf = (attr: string) =>
            new Set(
              branchItems
                .map((item) => String(effectiveAttrs(item, draft)[attr] ?? ""))
                .filter((entry) => !isPlaceholderValue(entry)),
            );
          // Where the strata cannot say what was absorbed, the spine can: a constant BAND
          // level was carved into the zone. A constant COLUMN level is kept — it is the column.
          const isAbsorbed = (level: SpineLevel) =>
            valuesOf(level.attr).size <= 1 &&
            (level.orient === "vertical" ||
              (own !== undefined &&
                (absorbed.has(fold(level.attr)) || absorbed.has(fold(level.label ?? "")))));
          const present = levelsForScope(draft.spine, draft.scopedLevelOrder, [value]).filter(
            (level) =>
              level.attr === root.attr ||
              branchItems.some(
                (item) =>
                  !isPlaceholderValue(String(effectiveAttrs(item, draft)[level.attr] ?? "")),
              ),
          );
          const kept = present.filter((level) => level.attr === root.attr || !isAbsorbed(level));
          // A zone a band carved out of a brand ("BT-Co Small pack") already names both: a level
          // every product in it shares ("Mega") is no block, so the first level that splits the
          // zone takes cdt1 and the brand keeps its sub-lines. A horizontal wall reads its own.
          const splits = (level: SpineLevel) => valuesOf(level.attr).size > 1;
          // Only a level the whole section holds one value of: a size row carved out of a brand
          // ("PT-Bo large", all Select-A-Size beside the small row's Prints) keeps the section's
          // own cdt1, or its sibling rows would ship cdt1 on two different levels.
          const sectionNames = new Set(
            strata
              .filter((stratum) => own !== undefined && stratum.section === own.section)
              .map((stratum) => stratum.cdt0),
          );
          const splitsSection = (level: SpineLevel) =>
            new Set(
              items
                .filter((item) =>
                  sectionNames.has(String(effectiveAttrs(item, draft)[root.attr] ?? "")),
                )
                .map((item) => String(effectiveAttrs(item, draft)[level.attr] ?? ""))
                .filter((entry) => !isPlaceholderValue(entry)),
            ).size > 1;
          // A size-row band ("PT-V large", band attribute "size (cut)") is its brand's rows,
          // not a brand of its own: it plans as its section does.
          const sizeRow = / \(cut\)$/.test(own?.bandAttr ?? "");
          const lift =
            !!own?.bandAttr &&
            !sizeRow &&
            !horizontal &&
            kept.length > 2 &&
            !splits(kept[1]) &&
            !splitsSection(kept[1]);
          const opening = lift ? kept.findIndex((level, at) => at > 1 && splits(level)) : -1;
          const unabsorbed =
            opening > 1
              ? [kept[0], kept[opening], ...kept.filter((_, at) => at > 0 && at !== opening)]
              : kept;
          // Where the next level is a band too, dropping would stand it up as cdt1 columns.
          // A horizontal wall has no columns: every tier below cdt0 is rows.
          const ordered =
            !horizontal && unabsorbed[1]?.orient === "vertical" ? present : unabsorbed;
          const tiers = [
            ordered.slice(0, 1),
            ordered.slice(1, 2),
            ordered.slice(2, 3),
            ordered.slice(3),
          ].map((levels) => levels.map((level) => level.attr));
          return {
            value,
            skus: items.filter(
              (item) => String(effectiveAttrs(item, draft)[root.attr] ?? "") === value,
            ).length,
            tiers,
            because: [
              "Preserve tree CDT0",
              "Preserve tree CDT1",
              "Preserve next tree level",
              "Retain remaining levels in tree order",
            ],
          };
        },
      )
    : [];
  const levels: CdtLevelPlan["levels"] = [0, 1, 2, 3].map((tier) => {
    const attrs = new Set(branches.flatMap((branch) => branch.tiers[tier]));
    const sources: SpineLevel[] = declared.filter((level) => attrs.has(level.attr));
    return {
      levels: sources,
      orient: tier === 2 ? "vertical" : "horizontal",
      role: tier < 2 ? "vertical" : "horizontal",
      because:
        tier < 2
          ? "Preserve tree grouping"
          : "Compress remaining tree levels without dropping attributes",
    };
  });
  return {
    levels,
    branches,
    // A horizontal root keeps the tree's tiers; only the column lift is off.
    ...(horizontal ? { blocking: "horizontal" as const } : {}),
    // Both paths say what the wall read and why, so a vertical reading is never silent.
    ...(draft.wallBlocking ? { wallReading: wallReadingOf(draft.wallBlocking) } : {}),
    verticality: [],
    bandedness: [],
    needsRuling: [],
    demoted: [],
    inert: [],
    warnings: horizontal ? horizontalWarnings(horizontal) : [],
  };
};
